import { apiClient } from '../../../api/client'
import { downloadXlsx } from '../../../lib/download'
import type {
  PageResponse,
  TicketApprove,
  TicketLookups,
  TicketQuery,
  TicketReject,
  TicketRequestCreate,
  TicketRequestDetail,
  TicketRequestListItem,
  TicketSummary,
} from '../types'

/** Sayfalı/filtreli/aranabilir liste. */
export async function getTicketRequests(
  query: TicketQuery,
): Promise<PageResponse<TicketRequestListItem>> {
  const params: Record<string, string | number> = {
    page: query.page ?? 0,
    size: query.size ?? 50,
  }
  if (query.statusId != null) params.statusId = query.statusId
  if (query.officeId != null) params.officeId = query.officeId
  if (query.typeId != null) params.typeId = query.typeId
  if (query.q && query.q.trim()) params.q = query.q.trim()
  if (query.exactPnr && query.exactPnr.trim()) params.exactPnr = query.exactPnr.trim()
  if (query.exactName && query.exactName.trim()) params.exactName = query.exactName.trim()

  const { data } = await apiClient.get<PageResponse<TicketRequestListItem>>(
    '/management/ticket-requests',
    {
      params,
    },
  )
  return data
}

/**
 * Talep listesini Excel (.xlsx) olarak indirir (035). Liste ile aynı filtreler; sunucu **tüm** filtreli
 * kayıtları döndürür (sayfalama yok). İndirme mekaniği ve dosya adı ortak {@link downloadXlsx}
 * yardımcısındadır (boş filtreler gönderilmez; ad sunucunun Content-Disposition'ından okunur).
 */
export async function exportTicketRequests(query: TicketQuery): Promise<void> {
  await downloadXlsx(
    '/management/ticket-requests/export',
    {
      statusId: query.statusId,
      officeId: query.officeId,
      typeId: query.typeId,
      q: query.q,
      exactPnr: query.exactPnr,
      exactName: query.exactName,
    },
    'talep-listesi.xlsx',
  )
}

/**
 * Tek talebin tam detayı (salt-okuma). 404 → hata (istemci "bulunamadı" gösterir). Özel kalem (037):
 * kendi-dışı bir talebe erişim için, listede kullanılan tam kimlik (tam PNR / tam Ad-Soyad) taşınır;
 * backend kanıt olarak doğrular (aksi halde 404). Diğer tier'larda bu alanlar yok sayılır.
 */
export async function getTicketRequestDetail(
  id: number,
  exactPnr?: string,
  exactName?: string,
): Promise<TicketRequestDetail> {
  const params: Record<string, string> = {}
  if (exactPnr && exactPnr.trim()) params.exactPnr = exactPnr.trim()
  if (exactName && exactName.trim()) params.exactName = exactName.trim()
  const { data } = await apiClient.get<TicketRequestDetail>(
    `/management/ticket-requests/${id}`,
    { params },
  )
  return data
}

/** KPI özeti (kapsam-geneli; filtreden bağımsız). */
export async function getTicketSummary(): Promise<TicketSummary> {
  const { data } = await apiClient.get<TicketSummary>('/management/ticket-requests/summary')
  return data
}

/** Filtre seçenekleri (durum/makam/tür). */
export async function getTicketLookups(): Promise<TicketLookups> {
  const { data } = await apiClient.get<TicketLookups>('/management/ticket-requests/lookups')
  return data
}

/** Yeni talep oluştur (201 → oluşturulan kaydın liste satırı). */
export async function createTicketRequest(
  body: TicketRequestCreate,
): Promise<TicketRequestListItem> {
  const { data } = await apiClient.post<TicketRequestListItem>('/management/ticket-requests', body)
  return data
}

/** Talebi onayla (Beklemede→Onaylandı). 200 → güncel liste satırı. */
export async function approveTicket(
  id: number,
  body: TicketApprove,
): Promise<TicketRequestListItem> {
  const { data } = await apiClient.put<TicketRequestListItem>(
    `/management/ticket-requests/${id}/approve`,
    body,
  )
  return data
}

/** Talebi reddet (Beklemede→Reddedildi; gerekçe zorunlu, "Diğer"→not zorunlu). 200 → güncel satır. */
export async function rejectTicket(id: number, body: TicketReject): Promise<TicketRequestListItem> {
  const { data } = await apiClient.put<TicketRequestListItem>(
    `/management/ticket-requests/${id}/reject`,
    body,
  )
  return data
}
